import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Search, BadgeCheck, Clock, CalendarDays, X } from "lucide-react";
import { toast } from "sonner";
import VendorCard from "@/components/VendorCard";
import { CATEGORIES, CATEGORY_FILTERS } from "@/lib/categories";
import { formatEventDate, isBookableEvent } from "@/lib/dates";

const CATS = [{ key: "all", label: "All" }, ...CATEGORIES.map(c => ({ key: c.key, label: c.short || c.label }))];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const eventId = params.get("event") || "";
  const [event, setEvent] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [location, setLocation] = useState(params.get("location") || params.get("city") || "");
  const [maxPrice, setMaxPrice] = useState([15000]);
  const [specialty, setSpecialty] = useState(params.get("specialty") || "");
  const [minPax, setMinPax] = useState(params.get("min_passenger_capacity") || "");
  const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified") === "true");
  const [eventDate, setEventDate] = useState(params.get("date") || "");
  const [period, setPeriod] = useState(params.get("period") || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const catFilters = CATEGORY_FILTERS[category] || [];

  // Event-specific browsing: the event's stored date drives the availability
  // filter so unavailable vendors are excluded by default.
  useEffect(() => {
    if (!eventId) { setEvent(null); return; }
    let cancelled = false;
    api.get(`/events/${eventId}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (!isBookableEvent(data)) {
          toast.error("That event is no longer bookable.");
          clearEvent();
          return;
        }
        setEvent(data);
        setEventDate((data.date || "").slice(0, 10));
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("That event could not be found.");
        clearEvent();
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const clearEvent = () => {
    const p = new URLSearchParams(params);
    p.delete("event");
    setParams(p, { replace: true });
    setEvent(null);
    setEventDate("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const query = { limit: 60 };
      if (category !== "all") query.category = category;
      if (q) query.q = q;
      if (location) query.location = location;
      if (maxPrice[0] < 15000) query.max_price = maxPrice[0];
      if (specialty) query.specialty = specialty;
      if (minPax) query.min_passenger_capacity = minPax;
      if (verifiedOnly) query.verified = true;
      if (eventDate) query.date = eventDate;
      if (eventDate && period) query.period = period;
      try {
        const { data } = await api.get("/vendors", { params: query });
        setItems(data);
      } catch { setItems([]); }
      setLoading(false);
    };
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [category, q, location, maxPrice, specialty, minPax, verifiedOnly, eventDate, period]);

  const setCat = (c) => {
    const p = new URLSearchParams(params);
    if (c === "all") p.delete("category"); else p.set("category", c);
    p.delete("specialty");
    p.delete("min_passenger_capacity");
    setSpecialty(""); setMinPax("");
    setParams(p);
  };

  const toggleVerified = () => {
    const next = !verifiedOnly;
    setVerifiedOnly(next);
    const p = new URLSearchParams(params);
    if (next) p.set("verified", "true"); else p.delete("verified");
    setParams(p);
  };

  return (
    <div data-testid="browse-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="eyebrow mb-3">The Collective</div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight tracking-tight mb-8 sm:mb-10">Browse vendors</h1>

      {event && (
        <div data-testid="browse-event-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[color:var(--border)] bg-white px-4 py-3">
          <CalendarDays className="w-4 h-4 text-[color:var(--accent)] shrink-0" />
          <div className="text-sm flex-1 min-w-0">
            <span className="text-[color:var(--text-muted)]">Showing vendors available for </span>
            <span className="font-semibold" data-testid="browse-event-title">{event.title}</span>
            <span className="text-[color:var(--text-muted)]"> · {formatEventDate(event.date)}{event.location ? ` · ${event.location}` : ""}</span>
          </div>
          <button type="button" onClick={clearEvent} data-testid="browse-clear-event" className="inline-flex items-center gap-1 text-xs text-[color:var(--text-muted)] hover:text-neutral-900 self-start sm:self-auto">
            <X className="w-3.5 h-3.5" /> Clear event
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 -mx-1 px-1 overflow-x-auto" data-testid="category-filters">
        {CATS.map((c) => (
          <button key={c.key} data-testid={`filter-${c.key}`} onClick={() => setCat(c.key)}
            className={`chip whitespace-nowrap ${category === c.key ? "active" : ""}`}>{c.label}</button>
        ))}
        <button
          type="button"
          data-testid="filter-verified"
          onClick={toggleVerified}
          aria-pressed={verifiedOnly}
          className={`chip whitespace-nowrap inline-flex items-center gap-1.5 ${verifiedOnly ? "active" : ""}`}
        >
          <BadgeCheck className="w-3.5 h-3.5" /> Verified only
        </button>
      </div>

      {/* Category-specific sub-filters */}
      {catFilters.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-8" data-testid="category-subfilters">
          {catFilters.map((f) => (
            <select
              key={f.key}
              data-testid={`subfilter-${f.key}`}
              value={f.key === "specialty" ? specialty : (f.key === "min_passenger_capacity" ? minPax : "")}
              onChange={(e) => (f.key === "specialty" ? setSpecialty(e.target.value) : setMinPax(e.target.value))}
              className="bg-white border border-[#E8E2D9] rounded-full px-4 py-2 text-sm h-10"
            >
              <option value="">{f.label}: any</option>
              {f.options.map((o) => <option key={o} value={o}>{typeof o === "number" ? `${o}${f.suffix || ""}` : o}</option>)}
            </select>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
        <aside className="lg:col-span-4 xl:col-span-3 space-y-6 sm:space-y-8 grid sm:grid-cols-2 lg:grid-cols-1 gap-6 lg:gap-0 lg:space-y-8">
          <div>
            <div className="eyebrow mb-3">Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
              <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search vendors" className="pl-10 bg-white border-[#E8E2D9] h-11 rounded-md" />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Event location</div>
            <Input data-testid="location-input" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Alexandria, VA or 22311" className="bg-white border-[#E8E2D9] h-11 rounded-md" />
          </div>
          <div>
            <div className="eyebrow mb-3 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Event date</div>
            <Input
              type="date"
              data-testid="event-date-input"
              value={eventDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setEventDate(e.target.value)}
              disabled={!!event}
              title={event ? "Date is set by the selected event" : undefined}
              className="bg-white border-[#E8E2D9] h-11 rounded-md"
            />
            {event && <p className="mt-1 text-[11px] text-[color:var(--text-muted)]" data-testid="event-date-locked-note">Set by your event — change it from the event page.</p>}
            {eventDate && (
              <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid="event-period-picker">
                {[
                  { key: "", label: "Any" },
                  { key: "am", label: "AM" },
                  { key: "pm", label: "PM" },
                ].map(p => (
                  <button
                    key={p.key || "any"}
                    data-testid={`event-period-${p.key || "any"}`}
                    onClick={() => setPeriod(p.key)}
                    className={`chip whitespace-nowrap justify-center ${period === p.key ? "active" : ""}`}
                  >{p.label}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="eyebrow mb-3">Max price ${maxPrice[0].toLocaleString()}</div>
            <Slider data-testid="price-slider" value={maxPrice} onValueChange={setMaxPrice} min={100} max={15000} step={100} />
          </div>
        </aside>

        <div className="lg:col-span-8 xl:col-span-9">
          {loading ? (
            <div className="text-[#5C5C5C]" data-testid="loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-[#5C5C5C]" data-testid="no-results">No vendors match your search.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6" data-testid="vendors-grid">
              {items.map((v) => (
                <VendorCard key={v.vendor_id} v={v} eventId={event ? event.event_id : ""} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
